import { z } from "zod";
import { catalogueLocale, exerciseCategory, muscleBodyRegion, muscleMovementType, } from "./vocabulary";
/**
 * The filter vocabulary, with counts.
 *
 * ── WHY THE CLIENT ASKS THE SERVER FOR THIS INSTEAD OF SHIPPING IT ──────────
 * Every label here is CATALOGUE text — `Cable` is `Crossover` in a Brazilian
 * gym and `Hamstrings` is `Posteriores`, and those strings live in
 * `catalog_equipment_i18n` and `catalog_muscle_i18n`. A client-side copy would
 * be a second Portuguese vocabulary to keep in agreement with the database's,
 * and the way that fails is a filter chip reading `Cabo` next to results named
 * `Crossover`.
 *
 * The counts are here for one specific reason, spelled out on `equipmentCount`
 * below: they are how the prototype's count/filter inconsistency is resolved
 * VISIBLY rather than by assertion.
 *
 * ── THIS RESPONSE IS STATIC FOR THE LIFE OF A SEED ─────────────────────────
 * The catalogue is an immutable seed. Nothing a user does changes a
 * number in it, so the route answers with a long `Cache-Control` and the
 * Worker memoizes it per isolate. Re-deriving it per request would be 31 + 12
 * counting subqueries against a table that has not changed since ingestion.
 */
export const filtersQuery = z.object({ locale: catalogueLocale.optional() });
export const muscleFilter = z.object({
    id: z.int().positive(),
    /** Localized through the same three-level fallback as everything else. */
    name: z.string().min(1),
    bodyRegion: muscleBodyRegion,
    movementType: muscleMovementType,
    /**
     * Exercises that work this muscle in EITHER role, which is exactly what the
     * `muscleId` filter returns. Count and filter agree by construction.
     */
    exerciseCount: z.int().nonnegative(),
});
export const equipmentFilter = z.object({
    id: z.int().positive(),
    name: z.string().min(1),
    /**
     * Exercises where this is the primary **or** the secondary equipment.
     *
     * The prototype counted this way and filtered on the primary column only.
     * The disagreement is not academic: `Head Harness` is primary equipment for
     * NO exercise and secondary for seven, so it appeared in the filter list
     * showing "7" and returned nothing when tapped. Both sides now include
     * secondary — see `exercise-list.ts`'s note on `equipmentId`.
     */
    exerciseCount: z.int().nonnegative(),
});
export const categoryFilter = z.object({
    category: exerciseCategory,
    /**
     * NOT localized, and deliberately not carried here. The category's display
     * name is one of three fixed words that the APP owns in its own catalogue
     * (`catalog.category.strength` and friends) — there is no
     * `catalog_category_i18n` table, so inventing a `name` field here would mean
     * the server making up display text, which `D27` forbids everywhere else.
     */
    exerciseCount: z.int().nonnegative(),
});
export const filtersOutput = z.object({
    muscles: z.array(muscleFilter),
    equipment: z.array(equipmentFilter),
    categories: z.array(categoryFilter),
    /** 3,001 today. The list screen shows it before any filter is applied. */
    totalExercises: z.int().nonnegative(),
});
//# sourceMappingURL=filters.js.map